import { type PrimitiveAtom } from 'jotai'
import React from 'react'
import { type SubmitHandler, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { type Promisable } from 'type-fest'

import {
  StyledButton,
  type StyledButtonProps,
} from '~/components/elements/StyledButton'
import { useTurnOffAtom, useTurnOnAtom } from '~/hooks/jotai'

import { isVisibleLoadDialogAtom, isVisibleSaveDialogAtom } from '../atoms'
import { type InputValues, type SavedFilter } from '../utils'

export type EditFilterDialogActionsProps<TFilterItem> = {
  filters: SavedFilter[]
  isVisibleEditDialogAtom: PrimitiveAtom<boolean>
  onExecute?: (filterGroups: TFilterItem[][] | undefined) => Promisable<void>
}

export const EditFilterDialogActions = <TFilterItem,>({
  filters,
  isVisibleEditDialogAtom,
  onExecute,
}: EditFilterDialogActionsProps<TFilterItem>) => {
  const { t } = useTranslation()
  const {
    formState: { isDirty, isValid },
    handleSubmit,
    setValue,
    watch,
  } = useFormContext<InputValues<TFilterItem>>()

  const handleClearButtonClick = React.useCallback<
    EH<StyledButtonProps, 'onClick'>
  >(() => {
    setValue('filterGroups', [])
  }, [setValue])

  const isLoadButtonDisabled = filters.length === 0
  const openLoadDialog = useTurnOnAtom(isVisibleLoadDialogAtom)

  const filterGroups = watch('filterGroups')
  const isSaveButtonDisabled = !isValid || filterGroups.length === 0
  const openSaveDialog = useTurnOnAtom(isVisibleSaveDialogAtom)

  const isExecuteButtonDisabled = !isValid
  const closeEditDialog = useTurnOffAtom(isVisibleEditDialogAtom)
  const handleValid = React.useCallback<
    SubmitHandler<InputValues<TFilterItem>>
  >(
    async ({ filterGroups }) => {
      if (filterGroups.length === 0) {
        onExecute?.(undefined)
        closeEditDialog()

        return
      }

      if (!isDirty) {
        closeEditDialog()

        return
      }

      await onExecute?.(filterGroups)

      closeEditDialog()
    },
    [closeEditDialog, isDirty, onExecute],
  )

  return (
    <>
      <StyledButton
        buttonStyle="light"
        fillMode="flat"
        onClick={handleClearButtonClick}
      >
        {t('clear_conditions')}
      </StyledButton>

      <StyledButton
        buttonStyle="light"
        disabled={isLoadButtonDisabled}
        fillMode="outline"
        icon="folder-open"
        onClick={openLoadDialog}
      >
        {t('load_conditions')}
      </StyledButton>

      <StyledButton
        buttonStyle="light"
        disabled={isSaveButtonDisabled}
        fillMode="outline"
        icon="save"
        onClick={openSaveDialog}
      >
        {t('save_2')}
      </StyledButton>

      <StyledButton
        disabled={isExecuteButtonDisabled}
        icon="zoom"
        onClick={handleSubmit(handleValid)}
      >
        {t('apply')}
      </StyledButton>
    </>
  )
}
